import * as React from 'react'

import { Box, Button, DoubleRangeSlider } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'

import { ActivatePayload } from 'modules/app_store/lib/SubscriptionAddonInstaller'
import { RatingDefaultValues } from 'modules/custom_resources/store/collections/customFields/fields'
import schemas from 'modules/custom_resources/store/collections/customResources/schemas'
import Schema from 'modules/custom_resources/store/models/customResources/Schema'

type DoubleRangeSliderProps = React.ComponentProps<typeof DoubleRangeSlider>

interface Props {
  onSuccess: (payload?: ActivatePayload | null | undefined) => Promise<boolean>
  onClose: () => void
}

const ActivateCompetencies = ({ onSuccess, onClose }: Props) => {
  const [range, setRange] = React.useState<[number, number]>([
    RatingDefaultValues.minValue,
    RatingDefaultValues.maxValue,
  ])

  const { loading } = useAsyncData(async () => {
    // We don't want the user to select the rating if he/she had previously
    // installed competencies. If it's the case, we fetch the schema and submit
    // it directly
    await schemas.fetch({
      data: {
        topic_names: [Schema.TOPIC_NAMES.performance],
        visible: false,
      },
      remove: false,
    })

    const hasCompetenciesSchemas = !!schemas.filter({
      name: Schema.SCHEMA_NAMES.competencies,
    }).length

    if (hasCompetenciesSchemas) {
      onSuccess()
      onClose()
    }
  }, [])

  const onSelectRange: DoubleRangeSliderProps['onChange'] = ([min, max]) => {
    const minValue = Math.round(min)
    const maxValue = Math.round(max)

    if (minValue === maxValue) return

    setRange([minValue, maxValue])
  }

  const onSubmit = async () => {
    const [minValue, maxValue] = range
    onSuccess({
      min_rating: minValue,
      max_rating: maxValue,
    })
    onClose()
  }

  const hasCompetenciesSchemas = !!schemas.filter({
    name: Schema.SCHEMA_NAMES.competencies,
  }).length

  if (loading) return null

  return (
    <Modal
      illustration="settings"
      title={i18n.t('app_store_v2.apps.competencies_custom_table.install_modal.title')}
      description={i18n.t('app_store_v2.apps.competencies_custom_table.install_modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading} submit>
            {i18n.t('app_store_v2.apps.competencies_custom_table.install_modal.submit')}
          </Button>
        </>
      }
    >
      <Box paddingTop="s12" paddingBottom="s24">
        <DoubleRangeSlider
          min={RatingDefaultValues.minValue}
          max={RatingDefaultValues.maxValue}
          value={range}
          onChange={onSelectRange}
          readonly={hasCompetenciesSchemas}
        />
      </Box>
    </Modal>
  )
}

export default observer(ActivateCompetencies)
